import { useEffect, useRef } from "react";

interface SetupConversation {
  id: number;
  kind: string;
  createdAt: string;
  updatedAt?: string | null;
}

interface Options {
  enabled: boolean;
  workspaceId: string;
  conversations: SetupConversation[];
  currentId: number | string | null;
  inventoryLoaded: boolean;
  isLoading: boolean;
  suppressAutoSelection?: boolean;
  select: (id: number) => unknown;
}

/** Resume an existing setup chat after inventory loads instead of opening a blank chat. */
export function useWorkspaceOnboardingChatSelection({
  enabled,
  workspaceId,
  conversations,
  currentId,
  inventoryLoaded,
  isLoading,
  suppressAutoSelection = false,
  select,
}: Options): void {
  const initializedWorkspaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !inventoryLoaded || isLoading) return;
    if (initializedWorkspaceRef.current === workspaceId) return;
    initializedWorkspaceRef.current = workspaceId;
    if (suppressAutoSelection) return;
    if (currentId !== null) return;

    const setupConversation = conversations
      .filter((conversation) => conversation.kind === "chat")
      .sort(
        (left, right) =>
          (right.updatedAt || right.createdAt).localeCompare(
            left.updatedAt || left.createdAt,
          ) || right.id - left.id,
      )[0];

    if (setupConversation) void select(setupConversation.id);
  }, [
    enabled,
    workspaceId,
    conversations,
    currentId,
    inventoryLoaded,
    isLoading,
    suppressAutoSelection,
    select,
  ]);
}
